import { type OptionSelectionProps } from '@shared/components';
import { type ReactNode } from 'react';

import {
  type ExamLocationRequestRoomComputerInfoFeatures,
  type ExamLocationRequestRoomComputerInfoFeatureTypes,
} from './schema';

export interface ComputerFeatureCheckConfig {
  key: ExamLocationRequestRoomComputerInfoFeatures;
  featureType: ExamLocationRequestRoomComputerInfoFeatureTypes;
  label: string;
  options: (OptionSelectionProps & { endAdornment?: ReactNode })[];
  required?: boolean;
  withOthers?: boolean;
}

export const COMPUTER_DETAILS_FEATURES: ComputerFeatureCheckConfig[] = [
  {
    key: 'monitor',
    featureType: 'computerDetails',
    label: '1.หน้าจอ Monitor 17 นิ้ว ขึ้นไป',
    options: [
      { label: 'น้อยกว่า 17 นิ้ว', value: 'lt-17-inch' },
      { label: '17 นิ้วขึ้นไป', value: 'ge-17-inch' },
    ],
    withOthers: true,
    required: true,
  },
  {
    key: 'cpu',
    featureType: 'computerDetails',
    label: '2.การประมวลผล CPU: Intel i3 2.0 GHz ขึ้นไป หรือเทียบเท่า',
    options: [
      {
        label: 'Intel core i3 (หรือ เทียบเท่า) ความเร็ว 2.0 GHz ขึ้นไป',
        value: 'intel-core-i3-ge-2ghz',
      },
      {
        label: 'Intel core i5 (หรือ เทียบเท่า) ความเร็ว 2.0 GHz ขึ้นไป',
        value: 'intel-core-i5-ge-2ghz',
      },
      {
        label: 'Intel core i7 (หรือ เทียบเท่า) ความเร็ว 2.0 GHz ขึ้นไป',
        value: 'intel-core-i7-ge-2ghz',
      },
    ],
    required: true,
    withOthers: true,
  },
  {
    key: 'ram',
    featureType: 'computerDetails',
    label: '3.หน่วยความจำ RAM แนะนำ 4 GB ขึ้นไป',
    options: [
      { label: 'น้อยกว่า 4 GB', value: 'lt-4gb' },
      { label: '4 GB', value: '4gb' },
      { label: '8 GB', value: '8gb' },
      { label: '16 GB', value: '16gb' },
      { label: 'มากกว่า 16 GB', value: 'gt-16gb' },
    ],
    required: true,
    withOthers: true,
  },
  {
    key: 'os',
    featureType: 'computerDetails',
    label: '4.ระบบปฎิบัติการ (Operating System)',
    options: [
      { label: 'Windows 10', value: 'windows-10' },
      { label: 'Windows 11', value: 'windows-11' },
      { label: 'macOS 10.14 ขึ้นไป', value: 'macos-ge-10.14' },
    ],
    required: true,
    withOthers: true,
  },
  {
    key: 'storage',
    featureType: 'computerDetails',
    // eslint-disable-next-line @cspell/spellchecker
    label: '5.พื้นที่จัดเก็บข้อมูล Harddisk: 500 GB ขึ้นไป (พื้นที่ว่าง ไม่ต่ำกว่า 2 GB)',
    options: [
      { label: '500 GB', value: '500gb' },
      { label: '1 TB', value: '1tb' },
      { label: '2 TB', value: '2tb' },
    ],
    required: true,
    withOthers: true,
  },
  {
    key: 'webBrowser',
    featureType: 'computerDetails',
    label: '6.Web Browser สำหรับเปิดเว็บไซต์ทั่วไป',
    options: [
      { label: 'Google Chrome', value: 'google-chrome' },
      { label: 'Microsoft Edge', value: 'edge' },
      { label: 'Mozilla Firefox', value: 'firefox' },
      { label: 'Safari (สำหรับ macOS)', value: 'safari' },
    ],
    required: true,
    withOthers: true,
  },
  {
    key: 'sebVersionStatus',
    featureType: 'computerDetails',
    label: '7.Safe Exam Browser: ที่ใช้ในการสอบ',
    options: [
      { label: 'Safe Exam Browser เวอร์ชัน 3.9.3 ขึ้นไป', value: 'seb-ge-3.9.3' },
      { label: 'Safe Exam Browser เวอร์ชันต่ำกว่า 3.9.3', value: 'seb-lt-3.9.3' },
    ],
    required: true,
  },
  {
    key: 'osUpdateCadence',
    featureType: 'computerDetails',
    label: '8.ความถี่ในการอัปเดตระบบปฏิบัติการ (Operating System)',
    options: [
      { label: 'ทุกเดือน', value: 'monthly' },
      { label: 'ทุกไตรมาส', value: 'quarterly' },
      { label: 'ทุก 6 เดือน', value: 'semiannual' },
    ],
    required: true,
    withOthers: true,
  },
];

export const INTERNET_DETAILS_FEATURES: ComputerFeatureCheckConfig[] = [
  {
    key: 'internetSpeed',
    featureType: 'internetDetails',
    label:
      // eslint-disable-next-line @cspell/spellchecker
      '1.คอมพิวเตอร์ต้องเชื่อมต่ออินเตอร์เน็ตขั้นต่ำ ดาวน์โหลดไม่น้อยกว่า 100 Mbps / อัปโหลดไม่น้อยกว่า 50 Mbps',
    options: [
      // eslint-disable-next-line @cspell/spellchecker
      { label: '100 Mbps / 50 Mbps', value: '100-50' },
      // eslint-disable-next-line @cspell/spellchecker
      { label: '100 Mbps / 100 Mbps', value: '100-100' },
      // eslint-disable-next-line @cspell/spellchecker
      { label: '200 Mbps / 50 Mbps', value: '200-50' },
      // eslint-disable-next-line @cspell/spellchecker
      { label: '200 Mbps / 100 Mbps', value: '200-100' },
    ],
    required: true,
    withOthers: true,
  },
];

export const SECURITY_DETAILS_FEATURES: ComputerFeatureCheckConfig[] = [
  {
    key: 'antivirusProgram',
    featureType: 'securityDetails',
    label: '1.โปรแกรมป้องกันไวรัส (Antivirus) ที่ติดตั้งในเครื่องคอมพิวเตอร์',
    options: [
      { label: 'Windows Defender (มาพร้อม Windows)', value: 'windows-defender' },
      { label: 'Norton Antivirus', value: 'norton' },
      // eslint-disable-next-line @cspell/spellchecker
      { label: 'Kaspersky', value: 'kaspersky' },
      { label: 'Avast', value: 'avast' },
    ],
    required: true,
    withOthers: true,
  },
  {
    key: 'antivirusConfigForSEB',
    featureType: 'securityDetails',
    label: '2.มีการตั้งค่าโปรแกรมป้องกันไวรัสเพื่อไม่ให้บล็อก Safe Exam Browser แล้วหรือไม่',
    options: [
      {
        label: 'ตั้งค่าเรียบร้อย (เพิ่ม Safe Exam Browser ใน Exception/Whitelist)',
        value: 'allow-seb',
      },
      { label: 'ไม่มีโปรแกรมป้องกันไวรัส', value: 'not-installed' },
      { label: 'ไม่มีให้ตั้งค่าโปรแกรมป้องกันไวรัส', value: 'not-available' },
      { label: 'ยังไม่ได้ตั้งค่า', value: 'not-configured' },
    ],
    required: true,
  },
  {
    key: 'isSyncedWithThaiServer',
    featureType: 'securityDetails',
    label:
      '3.มีการตั้งค่าเวลาในระบบ โดยตรวจสอบเวลาเครื่องให้ตรงตาม GMT+7 (เวลาไทย) และเทียบกับเว็บไซต์แสดงเวลามาตรฐานของกองทัพเรือไทย (https://www.navy.mi.th/clock) แล้วหรือไม่',
    options: [
      { label: 'ใช่ มีการตั้งค่าตรงกับเวลาไทย', value: 'synced' },
      { label: 'ไม่ ยังไม่มีการตั้งค่าตรงกับเวลาไทย', value: 'not-synced' },
    ],
    required: true,
  },
  {
    key: 'usbPortCheck',
    featureType: 'securityDetails',
    label: '5.มีการตรวจสอบ USB Port และอุปกรณ์เชื่อมต่อ เหลือเพียง Mouse และ Keyboard เท่านั้น',
    options: [
      { label: 'มีการตรวจสอบแล้ว', value: 'checked' },
      { label: 'ยังไม่ได้มีการตรวจสอบ', value: 'not-checked' },
      { label: 'ไม่มีอุปกรณ์ USB เชื่อมต่อ', value: 'not-available' },
    ],
    required: true,
  },
  {
    key: 'antivirusUpdateCadence',
    featureType: 'securityDetails',
    label: '6.มีการอัปเดต Patch ของโปรแกรมป้องกันไวรัส อย่างสม่ำเสมอ',
    options: [
      { label: 'ทุกเดือน', value: 'monthly' },
      { label: 'ทุกไตรมาส', value: 'quarterly' },
      { label: 'ทุก 6 เดือน', value: 'semiannual' },
    ],
    required: true,
    withOthers: true,
  },
];
